<script setup lang="ts">
import { useLoggedInUserFlags } from "@/composables/use-logged-in-user-flags";
import ControlValidation from "@/components/control-validation.vue";
import { useControlValidation } from "@/composables/use-control-validation";
import type { ContractDTO } from "@/models/dtos/contract-dto";
import { HelpMessage } from "@/models/enums/help-message";
import type { CreateServiceOrderRequest } from "@/models/requests/create-service-order-request";
import { RouteName } from "@/router";
import { getCreateServiceOrderSchema } from "@/schemas/create-service-order-schema";
import { contractsService } from "@/services/contracts-service";
import { notificationsService } from "@/services/notifications-service";
import { serviceOrdersService } from "@/services/service-orders-service";
import { isEqual } from "lodash-es";
import { onBeforeMount, ref, toRefs } from "vue";
import { useRoute, useRouter } from "vue-router";
import { TableColumn } from "@/models/table-column";
import type { ServiceOrderDTO } from "@/models/dtos/service-order-dto";
import AppTable from "@/components/app-table.vue";
import { mdiUpload } from "@mdi/js";

const { userIsOneAdmin, userIsOneUser } = useLoggedInUserFlags();
const router = useRouter();
const activeRoute = useRoute();

function getDefaultFormData(
  contractId: number | string,
): CreateServiceOrderRequest {
  return {
    contractId,
    title: "",
    facilityName: "",
    address: "",
    postAddress: "",
    coordinates: {
      x: undefined,
      y: undefined,
    },
    orderNumber: undefined,
    description: undefined,
    customerReference: undefined,
    customer: {
      name: undefined,
      phone: undefined,
    },
    files: [],
  } as CreateServiceOrderRequest;
}

const props = defineProps<{ contractId: number | string }>();
const { contractId } = toRefs(props);

const pageLoading = ref(false);
const loading = ref(false);
const contract = ref<ContractDTO | undefined>();

const formData = ref(getDefaultFormData(contractId.value));
const serviceOrder = ref<ServiceOrderDTO>();

const files = ref<File[]>();
const filesColumns = ref([TableColumn.for("name", "Namn")]);

const { result, validate } = useControlValidation(() =>
  getCreateServiceOrderSchema(),
);

onBeforeMount(async () => {
  try {
    pageLoading.value = true;

    contract.value = await contractsService.getContract(contractId.value);
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av affären.",
      type: "error",
      error,
    });
  } finally {
    pageLoading.value = false;
  }
});

async function onCreate() {
  loading.value = true;

  try {
    const valid = validate(formData.value);
    if (!valid) return;

    formData.value.contractId = Number(contractId.value);

    const defaultCoordinates = getDefaultFormData("").coordinates;
    if (isEqual(formData.value.coordinates, defaultCoordinates)) {
      formData.value.coordinates = undefined;
    }

    serviceOrder.value = await serviceOrdersService.createServiceOrder(
      formData.value,
    );
    notificationsService.open({
      text: `Serviceärende för ${contract.value?.name} skapat.`,
      type: "success",
    });

    if (formData.value.files) {
      try {
        loading.value = true;

        for (let i = 0; i < formData.value.files.length; i++) {
          await serviceOrdersService.addFile(serviceOrder.value.id, {
            file: formData.value.files[i],
          });
        }
      } catch (error) {
        notificationsService.open({
          text: "Något gick fel vid uppladdning av filen.",
          type: "error",
          error,
        });
      } finally {
        loading.value = false;
      }
    }
    if (!userIsOneAdmin && userIsOneUser) {
      await router.push({ name: RouteName.PhoneService });
    } else {
      await router.push({
        name: RouteName.ServiceOrders,
        query: activeRoute.query,
      });
    }
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid skapning av serviceärendet.",
      type: "error",
      error,
    });
  } finally {
    loading.value = false;
  }
}

async function onUploadFile(event: Event) {
  if (event.target instanceof HTMLInputElement) {
    files.value = event?.target?.files ? Array.from(event?.target?.files) : [];

    for (let i = 0; i < files.value.length; i++) {
      const f = files.value[i];
      if (f && formData.value.files) {
        formData.value.files.push(f);
      }
    }

    event.target.value = "";
  }
}
</script>

<template>
  <section class="section px-0">
    <p class="title is-4">Skapa serviceärende för {{ contract?.name }}</p>

    <app-loader v-if="pageLoading"></app-loader>

    <div v-else class="columns">
      <div class="column is-8">
        <form @submit.prevent="onCreate">
          <div class="field">
            <label class="label" for="title">Rubrik</label>
            <control-validation
              :result="result"
              field="title"
              #="{ inputClass }"
            >
              <div class="control has-icons-right">
                <input
                  id="title"
                  v-model="formData.title"
                  class="input"
                  type="text"
                  name="title"
                  :class="inputClass"
                />
                <span class="input-hint">{{ HelpMessage.TITLE }}</span>
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="facilityName">Anläggningens namn</label>
            <control-validation
              :result="result"
              field="facilityName"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="facilityName"
                  v-model="formData.facilityName"
                  class="input"
                  type="text"
                  name="facilityName"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="address">Adress</label>
            <control-validation
              :result="result"
              field="address"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="address"
                  v-model="formData.address"
                  class="input"
                  type="text"
                  name="address"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="postAddress">Postadress</label>
            <control-validation
              :result="result"
              field="postAddress"
              #="{ inputClass }"
            >
              <div class="control">
                <input
                  id="postAddress"
                  v-model="formData.postAddress"
                  class="input"
                  type="text"
                  name="postAddress"
                  :class="inputClass"
                />
              </div>
            </control-validation>
          </div>

          <div v-if="formData.coordinates" class="columns">
            <div class="column">
              <div class="field">
                <label class="label" for="coordinates.x"
                  >Kordinat X (RT90)</label
                >
                <control-validation
                  :result="result"
                  field="coordinates.x"
                  #="{ inputClass }"
                >
                  <div class="control has-icons-right">
                    <input
                      id="coordinates.x"
                      v-model="formData.coordinates.x"
                      class="input"
                      type="text"
                      name="coordinates.x"
                      :class="inputClass"
                    />
                    <span class="input-hint">{{ HelpMessage.RT90 }}</span>
                  </div>
                </control-validation>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <label class="label" for="coordinates.y"
                  >Kordinat Y (RT90)</label
                >
                <control-validation
                  :result="result"
                  field="coordinates.y"
                  #="{ inputClass }"
                >
                  <div class="control has-icons-right">
                    <input
                      id="coordinates.y"
                      v-model="formData.coordinates.y"
                      class="input"
                      type="text"
                      name="coordinates.y"
                      :class="inputClass"
                    />
                    <span class="input-hint">{{ HelpMessage.RT90 }}</span>
                  </div>
                </control-validation>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label" for="description">Beskrivning</label>
            <control-validation
              :result="result"
              field="description"
              #="{ inputClass }"
            >
              <div class="control has-icons-right">
                <textarea
                  id="description"
                  v-model="formData.description"
                  class="textarea"
                  name="description"
                  :class="inputClass"
                />
                <span class="input-hint">{{ HelpMessage.DESCRIPTION }}</span>
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="orderNumber"
              >Beställarens ärendenummer</label
            >
            <control-validation
              :result="result"
              field="orderNumber"
              #="{ inputClass }"
            >
              <div class="control has-icons-right">
                <input
                  id="orderNumber"
                  v-model="formData.orderNumber"
                  class="input"
                  type="text"
                  name="orderNumber"
                  :class="inputClass"
                />
                <span class="input-hint">{{ HelpMessage.ORDER_NUMBER }}</span>
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="customerReference">Referens</label>
            <control-validation
              :result="result"
              field="customerReference"
              #="{ inputClass }"
            >
              <div class="control has-icons-right">
                <input
                  id="customerReference"
                  v-model="formData.customerReference"
                  class="input"
                  type="text"
                  name="customerReference"
                  :class="inputClass"
                />
                <span class="input-hint">{{
                  HelpMessage.CUSTOMER_REFERENCE
                }}</span>
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="customer.name">Slutkund namn</label>
            <control-validation
              :result="result"
              field="customer.name"
              #="{ inputClass }"
            >
              <div class="control has-icons-right">
                <input
                  id="customer.name"
                  v-model="formData.customer!.name"
                  class="input"
                  type="text"
                  name="customer.name"
                  :class="inputClass"
                />
                <span class="input-hint">{{ HelpMessage.CUSTOMER_NAME }}</span>
              </div>
            </control-validation>
          </div>

          <div class="field">
            <label class="label" for="customer.phone">Slutkund telefon</label>
            <control-validation
              :result="result"
              field="customer.phone"
              #="{ inputClass }"
            >
              <div class="control has-icons-right">
                <input
                  id="customer.phone"
                  v-model="formData.customer!.phone"
                  class="input"
                  type="tel"
                  name="customer.phone"
                  :class="inputClass"
                />
                <span class="input-hint">{{ HelpMessage.CUSTOMER_PHONE }}</span>
              </div>
            </control-validation>
          </div>

          <div class="control">
            <button
              class="button is-primary"
              type="submit"
              :class="{ 'is-loading': loading }"
            >
              Skapa
            </button>
          </div>
        </form>
      </div>

      <div class="column is-aero">
        <section class="section notification is-light">
          <p class="title is-5">Filer</p>
          <div class="columns is-multiline">
            <div class="column is-12">
              <app-table :columns="filesColumns" :items="formData.files">
                <template #column:name="{ value }">
                  {{ value }}
                </template>
              </app-table>
            </div>
            <div class="column is-12">
              <div class="field">
                <div class="file has-name">
                  <label class="file-label" for="errorReportFile">
                    <input
                      id="errorReportFile"
                      class="file-input"
                      type="file"
                      name="errorReportFile"
                      multiple
                      @change="onUploadFile"
                    />
                    <span class="file-cta">
                      <span class="file-icon">
                        <app-icon :icon="mdiUpload" />
                      </span>
                      <span class="file-label">Lägg till filer </span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>
